import { ComparisonAnswers, CustomAnswerModel } from '../models/comparison/comparison-answers';
import { FormArray, FormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import {
  GroupSpecification,
  QuestionType,
  StepsQuestionsView,
  StepView,
  TypeQuestionView,
  TypeQuestionViewForm
} from '../models/client-questions';
import { FormBuilderService } from './form-builder.service';
import { UsersService } from "./users.service";

@Injectable()
export class FormService {
  public form: FormGroup;
  public steps: StepView[] = [];
  public currentStep = 0;

  constructor(private formBuilderService: FormBuilderService,
    private userService: UsersService) { }

  public initialize(stepsQuestionsView: StepsQuestionsView, answers: ComparisonAnswers = null): void {
    if (this.form) {
      return;
    }
    this.form = new FormGroup({ profiles: new FormArray([]) });
    this.initSteps(stepsQuestionsView.steps);
    this.initSpecificQuestions(stepsQuestionsView.specificQuestions);
    this.initProfileQuestions(stepsQuestionsView.questions);
    this.initQuestions(stepsQuestionsView.questions);

    if (answers) {
      this.setValues(answers);
    }
  }

  private setValues(answers: ComparisonAnswers): void {

    const specific = (this.form.get('specific') as FormArray);
    const profiles = (this.form.get('profiles') as FormArray);

    if (answers.specialAnswers) {
      for (let i = 0; i < specific.length; i++) {
        const answer = answers.specialAnswers.find(x => x.questionId === specific.get(`${i}.questionId`).value);
        if (answer) {
          specific.get(`${i}.answers`).patchValue(answer.answer, { emitEvent: false });
        }
      }
    }

    if (answers.profileAnswers) {
      if (profiles.length === 0) {
        (this.form.get('profiles') as FormArray)
          .push(this.getSpecificationForm(this.userService.stepQuestion.questions, GroupSpecification.PersonalInfo));
      }

      if (answers.profileAnswers.length > 1) {
        for (let i = 1; i < answers.profileAnswers.length; i++) {
          (this.form.get('profiles') as FormArray)
            .push(this.getSpecificationForm(this.userService.stepQuestion.questions, GroupSpecification.PersonalInfo));
        }
      }

      for (let i = 0; i < profiles.length; i++) {
        if (profiles.value && profiles.value[i]) {
          for (let j = 0; j < profiles.value[i].length; j++) {
            if (profiles.get(`${i}.${j}.questionId`)) {
              if (answers.profileAnswers[i]) {
                const answer = answers.profileAnswers[i].find(x => x.questionId === profiles.get(`${i}.${j}.questionId`).value);
                if (answer) {
                  profiles.get(`${i}.${j}.answers`).patchValue(this.getValue(answer));
                }
              }
            }
          }
        }
      }
    }

    if (answers.customAnswers || answers.profileAnswers) {
      for (let s = 0; s < this.steps.length; s++) {
        const step = (this.form.get(`step${s}`) as FormArray);
        for (let i = 0; i < step.length; i++) {
          if (step.get(`${i}.questionId`)) {
            const answer = answers.customAnswers ? answers.customAnswers.find(x => x.questionId === step.get(`${i}.questionId`).value) : undefined ||
              answers.profileAnswers ? answers.profileAnswers[0].find(x => x.questionId === step.get(`${i}.questionId`).value) : undefined;
            if (answer) {
              /*console.log(answer);
              console.log(step.get(`${i}`));*/
              step.get(`${i}.answers`).patchValue(this.getValue(answer), { emitEvent: false });
            }
          }
        }
      }
    }

    // this.form.updateValueAndValidity();

  }

  private getValue(answer: CustomAnswerModel): any {
    switch (answer.type) {
      case QuestionType.MultiSelect:
        return answer.optionsId;
      case QuestionType.Checkbox:
        return answer.optionsId;
      case QuestionType.DatePicker:
        return answer.date;
      case QuestionType.Digits:
        return answer.digit;
      case QuestionType.Text:
      case QuestionType.PhoneNumber:
        return answer.text;
      case QuestionType.YesNoRadioButton:
        return answer.optionId;
      case QuestionType.RadioButton:
        return answer.optionId;
      case QuestionType.Select:
        return answer.optionId;
      default:
        return null;
    }
  }


  private initSteps(steps: StepView[]): void {
    for (let i = 0; i < steps.length; i++) {
      this.steps[steps[i].step] = steps[i];
    }
  }

  private initSpecificQuestions(typeSpecificQuestions: TypeQuestionView[]): void {
    this.form.addControl('specific', new FormArray([]));
    for (let i = 0; i < typeSpecificQuestions.length; i++) {
      const questionForm = TypeQuestionViewForm.createTypeQuestionForm(typeSpecificQuestions[i]);
      questionForm.get('answers').valueChanges.subscribe(value => this.formBuilderService.handleSpecialAnswer(value, questionForm));
      (this.form.get('specific') as FormArray).push(questionForm);
    }
  }

  private initProfileQuestions(typeQuestions: TypeQuestionView[]): void {
    const formArray = new FormArray([]);
    for (const question of typeQuestions) {
      if (question.groupSpecification === GroupSpecification.PersonalInfo) {
        const questionForm = TypeQuestionViewForm.createTypeQuestionForm(question);
        questionForm.get('answers').valueChanges
          .subscribe(value => this.formBuilderService.handleProfileAnswer(value, questionForm));
        formArray.push(questionForm);
      }
    }
    (this.form.get('profiles') as FormArray).push(formArray);
  }

  private initQuestions(typeQuestions: TypeQuestionView[]): void {
    for (let i = 0; i < typeQuestions.length; i++) {
      const element: TypeQuestionView = typeQuestions[i];

      if (!this.form.contains('step' + element.step)) {
        this.form.addControl('step' + element.step, new FormArray([]));
      }
      const questionForm = TypeQuestionViewForm.createTypeQuestionForm(element);

      questionForm.get('answers').valueChanges
        .subscribe(value => this.formBuilderService.handleQuestionAnswer(value, questionForm));
      (this.form.get('step' + element.step) as FormArray).push(questionForm);
    }
  }

  public getSpecificationForm(typeQuestions: TypeQuestionView[], specification: GroupSpecification): FormArray {
    const step = this.steps.find(x => x.groupSpecification === specification);
    const formArray = new FormArray([]);
    typeQuestions.filter(x => x.step === step.step && !x.isMoreInsurant && !x.isExcluded)
      .sort((x, y) => x.order > y.order ? 1 : -1)
      .forEach(quesion => formArray.push(TypeQuestionViewForm.createTypeQuestionForm(quesion)));

    return formArray;
  }

  public isQuestionVisible(control: FormGroup): boolean {
    if (control.value.requiredOptionsId && control.value.requiredOptionsId.length !== 0) {
      const optionAnswerList = this.getOptionalAnswers(control.parent.value);
      return control.value.requiredOptionsId.some(x => optionAnswerList.includes(x));
    } else {
      return true;
    }
  }

  public isAnswerVisable(control: FormGroup, optionIds: number[]): boolean {
    if (optionIds && optionIds.length !== 0) {
      const optionAnswerList = this.getOptionalAnswers(control.parent.value);
      return optionIds.every(x => optionAnswerList.includes(x));
    } else {
      return true;
    }
  }

  private getOptionalAnswers(quetions: TypeQuestionView[]) {
    return [].concat.apply([], quetions.filter(x => x.question.type === QuestionType.MultiSelect
      || x.question.type === QuestionType.Checkbox
      || x.question.type === QuestionType.YesNoRadioButton
      || x.question.type === QuestionType.RadioButton
      || x.question.type === QuestionType.Select)
      .map(x => x.answers));
  }
}
